/*
 * Copyright (c) 2022
 * 
 * Created Date: Tuesday, September 20th 2022, 2:10:20 pm
 * Author: Muhammad Fajariansyah Ismail
 * 
 */

import Constant from 'utils/Constants'
import LoginButton from 'components/Auth/LoginButton'
import useAuthRepo from 'services/repo/useAuthRepo'
import useCurrentProfileFromTokenHooks from 'hooks/useCurrentProfileFromTokenHooks'
import useLanguageHooks from 'hooks/useLanguageHooks'
import useQueryParamHooks from 'hooks/useQueryParamHook'
import { ADMIN_ROUTE_PREFIX, APP_ROUTE_PREFIX, TRAINER_ROUTE_PREFIX } from 'routes/RoutePrefix'
import { AiOutlineEye, AiOutlineEyeInvisible } from 'react-icons/ai'
import { Card } from 'react-bootstrap'
import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import LocalStorageUtils from 'utils/LocalStorage'
import useCookieHook from 'hooks/useCookieHook'
import useUserLoginRedirectionHook from 'hooks/useUserLoginRedirectionHook'
import LunaAlert from 'utils/LunaAlert'
import { LanguageConst } from 'utils/Language'

const AuthPage = () => {

    const formSubmitRef = useRef(null)
    const navigation = useNavigate()
    const { t, ChangeLanguage } = useLanguageHooks()
    const { login } = useAuthRepo(false)
    const { userLoginRedirection } = useUserLoginRedirectionHook()

    const [isPasswordShown, setIsPasswordShown] = useState(false)
    const [password, setPassword] = useState('')
    const [username, setUsername] = useState('')
    const [errorText, setErrorText] = useState('')
    const [isLoading, setIsLoading] = useState(false)

    const search = useQueryParamHooks()
    const redirectUrl = search.get('redirectUrl')

    const { currentRole, currentProfile, refreshPrivilege } = useCurrentProfileFromTokenHooks(false)
    const [isCheckingToken, setIsCheckingToken] = useState(false)

    const { initalAuthCookie } = useCookieHook()

    useEffect(() => {
        setErrorText('')
    }, [username, password])

    useEffect(() => {
        if(LocalStorageUtils.jwtToken.get() !== '') {
            tryRelog()
        }
    }, [])

    const tryRelog = () => {
        LocalStorageUtils.loginCheckToken.set()
        setIsCheckingToken(true)
        refreshPrivilege()
        .catch(error => {
            setIsCheckingToken(false)
            LocalStorageUtils.jwtToken.clear()
        })
    }
    
    const onFormSubmit = (e) => {
        e.preventDefault();

        if(username === '' && password === '') return false;

        formSubmitRef.current?.username?.blur()
        formSubmitRef.current?.password?.blur()
        onLoginButtonPressed();

        return false;
    }
    const onLoginButtonPressed = async () => {

        if(isLoading) return false;
    
        setIsLoading(true)
        setErrorText('')
        let response = await login(username, password)
        if (response?.isSuccess) {
            try {
                let url = await userLoginRedirection( response?.data?.listLstLunaUserRoleDto, response?.data.listLstUserProfileTypeDto, response?.data.listUserBankChannelDto, redirectUrl)

                if(response?.data?.userLanguage === LanguageConst.EN) {
                    ChangeLanguage.toEnglish()
                } else {
                    ChangeLanguage.toIndonesian()
                }

                initalAuthCookie()
                navigation(url, { replace: true })
            } catch (error) {
                setIsLoading(false)
                setErrorText(t('authPage.failedLogin'))
            }
        } else {
            setIsLoading(false)
            setErrorText(t('authPage.failedLogin'))
            if(response?.error?.name === 'AxiosError') {
                LunaAlert.error({
                    title: t('authPage.failedLogin'),
                    description: `${response.message}`,
                    confirmButtonText: 'Ok',
                });
            }
        }
    }

    useEffect(() => {
        if (currentProfile !== null && currentRole !== null) {
            setIsCheckingToken(false)
            if (currentRole?.name?.toLowerCase() === Constant.role.TRAINEE.toLowerCase()) return navigation(APP_ROUTE_PREFIX, { replace: true })
            if (currentRole?.name?.toLowerCase() === Constant.role.TRAINER.toLowerCase()) return navigation(TRAINER_ROUTE_PREFIX, { replace: true })
            if (currentRole?.name?.toLowerCase() === Constant.role.ADMINBC.toLowerCase()) return navigation(ADMIN_ROUTE_PREFIX, { replace: true })
            if (currentRole?.name?.toLowerCase() === Constant.role.ADMIN.toLowerCase()) return navigation(ADMIN_ROUTE_PREFIX, { replace: true })
            if (currentRole?.name?.toLowerCase() === Constant.role.SUPERADMIN.toLowerCase()) return navigation(ADMIN_ROUTE_PREFIX, { replace: true })
        }
    }, [currentRole, currentProfile])

    const isLoginButtonDisabled = username.length === 0 || password.length === 0

    return <div className={`auth-container ${isCheckingToken ? 'checking-token' : 'no-checking'}`}>
            <Card className='auth-container-card' data-loading-text={t('authPage.connectingServer')}>
                <img src={Constant.assets.logo.primary} alt="LUNA" width={120} />
                <p className='auth-container-login-title'>{t('authPage.title')}</p>
                <p className='auth-container-login-subtitle'>{t('authPage.subtitle')}</p>

                <form onSubmit={onFormSubmit} ref={formSubmitRef}>
                    <div className='auth-login-input-container'>
                        <input className='auth-login-input'
                            placeholder={` `}
                            value={username}
                            name='username'
                            onChange={(e) => setUsername(e.target.value)} />
                        <span className='auth-login-floating-label'>{t('authPage.inputUsernameLabel')}</span>
                        <div className='auth-login-floating-dot-container'>
                            <div className='auth-login-floating-dot'></div>
                        </div>
                    </div>
                    <div className='auth-login-input-container'>
                        <input type={isPasswordShown ? 'text' : 'password'}
                            className='auth-login-input'
                            placeholder={` `}
                            name='password'
                            value={password}
                            onChange={(e) => setPassword(e.target.value)} />
                        <span className='auth-login-floating-label'>{t('authPage.inputPasswordLabel')}</span>
                        <div className='auth-login-floating-dot-container'>
                            <div className='auth-login-floating-dot'></div>
                        </div>
                        <div className='auth-login-shown-container' onClick={() => setIsPasswordShown(!isPasswordShown)}>
                            {isPasswordShown ? <AiOutlineEye /> : <AiOutlineEyeInvisible />}
                        </div>
                    </div>

                    <p className={`auth-login-error-text ${errorText === '' ? 'invisible' : ''}`}>{errorText === '' ? '' : errorText}</p>
                    <LoginButton text={t('authPage.buttonLogin')} isLoading={isLoading} disabled={isLoginButtonDisabled || isLoading} />

                </form>
                <div className='app-version'>
                    v{Constant.version}
                </div>
            </Card>
    </div>
}

export default AuthPage
